<template>
  <div class="store-list">
    <div
      class="store-card"
      :class="{ 'has-delete': showDelete }"
      v-for="store in storelist"
      :key="getStoreKey(store)"
      @click="$emit('openStore', store)"
    >
      <button
        v-if="showDelete"
        class="delete-button"
        @click.stop="$emit('removeStore', store)"
      >删除</button>
      <div class="store-details">
        <h3 class="store-name">
          <template
            v-for="(part, index) in highlightText(store.storename)"
            :key="'name-' + index"
          >
            <span
              :class="{ highlight: part.matched }"
            >{{ part.text }}</span>
          </template>
          <span
            v-if="showStatus"
            :class="['status-text', getStoreStatusClass(store)]"
          >（{{ getStoreStatusText(store) }}）</span>
        </h3>
        <p v-if="showStoreCity">城市: {{ store.cityName }}</p>
        <p class="store-code">
          编号:
          <template
            v-for="(part, index) in highlightText(store.storecode)"
            :key="'code-' + index"
          >
            <span
              :class="{ highlight: part.matched }"
            >{{ part.text }}</span>
          </template>
        </p>
        <p>
          地址:
          <template
            v-for="(part, index) in highlightText(store.address)"
            :key="'address-' + index"
          >
            <span
              :class="{ highlight: part.matched }"
            >{{ part.text }}</span>
          </template>
        </p>
        <p>电话: {{ store.phone }}</p>
        <p>营业时间: {{ formatBusinessHours(store) }}</p>
        <p v-if="getDenseReminder(store)" class="dense-reminder">
          <span class="dense-icon">ⓘ</span>{{ getDenseReminder(store) }}
        </p>
        <p v-if="showDistance && store.distance" class="distance">距离: <span class="distance-value">{{ formatDistance(store.distance) }}</span></p>
      </div>
      <span class="enter-arrow">›</span>
    </div>
  </div>
</template>

<script setup>
import {
  formatBusinessHours,
  formatDistance,
  getDenseReminder,
  getStoreKey,
  getStoreStatusClass,
  getStoreStatusText
} from '../common/js/store';
import { splitTextByKeyword } from '../common/js/search';

defineOptions({name: 'StoreList'});

const props = defineProps({
  storelist: {
    type: Array,
    default: () => []
  },
  showStoreCity: {
    type: Boolean,
    default: false
  },
  showDelete: {
    type: Boolean,
    default: false
  },
  showStatus: {
    type: Boolean,
    default: true
  },
  showDistance: {
    type: Boolean,
    default: true
  },
  keyword: {
    type: String,
    default: ''
  }
});

defineEmits(['openStore', 'removeStore']);

function highlightText (text) {
  return splitTextByKeyword(text, props.keyword);
}
</script>

<style scoped>
.store-list {
  background-color: var(--surface-bg);
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  padding: 2px 0 8px;
  box-sizing: border-box;
}

.store-card {
  position: relative;
  display: flex;
  width: calc(100% - 12px);
  min-height: 88px;
  margin: 6px;
  padding: 8px 7px;
  border: 1px solid var(--border-color);
  border-radius: 7px;
  box-sizing: border-box;
  background: var(--surface-bg);
  cursor: pointer;
}

.store-card .delete-button {
  position: absolute;
  top: 5px;
  right: 22px;
  min-width: 40px;
  height: 22px;
  border: 1px solid var(--border-strong);
  border-radius: 4px;
  background: var(--surface-bg);
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
  z-index: 2;
}

.store-card .delete-button:active {
  border-color: var(--accent-color);
  color: var(--accent-color);
}

.store-card:active {
  border-color: var(--accent-color);
  background: var(--active-bg);
}

.store-details {
  flex: 1;
  min-width: 0;
  padding-right: 19px;
}

.store-name {
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-main);
  line-height: 18px;
}

.store-name span {
  font-weight: inherit;
}

.store-card.has-delete .store-name {
  padding-right: 52px;
}

.status-text {
  font-size: 12px;
  font-weight: normal;
}

.open {
  color: var(--success-color);
}

.closed {
  color: var(--accent-color);
}

.store-details p {
  margin: 1px 0;
  color: var(--text-secondary);
  line-height: 16px;
}

.store-code {
  color: var(--text-normal);
}

.highlight {
  color: var(--accent-color);
  font-weight: 700;
}

.distance-value {
  font-weight: bold;
  color: var(--text-main);
}

.dense-reminder {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text-faint) !important;
}

.dense-icon {
  flex: 0 0 auto;
  font-size: 14px;
  line-height: 1;
}

.enter-arrow {
  position: absolute;
  top: 50%;
  right: 7px;
  transform: translateY(-50%);
  color: var(--text-faint);
  font-size: 24px;
  line-height: 1;
}
</style>
